import type { SeaGoingService } from './types/seafarer';

// ── Date helpers ──────────────────────────────────────────────────────────────

/** Parse a DD/MM/YYYY string to a UTC timestamp (ms). Returns null on bad input. */
export function parseDdMmYyyy(date: string | null | undefined): number | null {
  if (!date) return null;
  const parts = date.trim().split('/');
  if (parts.length !== 3) return null;
  const [d, m, y] = parts.map(Number);
  if (!d || !m || !y || y < 1900 || y > 2100) return null;
  const ts = Date.UTC(y, m - 1, d);
  return isNaN(ts) ? null : ts;
}

/**
 * Number of inclusive days between two DD/MM/YYYY dates.
 * Returns null if either date is unparseable or sign-off < sign-on.
 */
export function calcServiceDaysFromDates(from: string, to: string): number | null {
  const t0 = parseDdMmYyyy(from);
  const t1 = parseDdMmYyyy(to);
  if (t0 === null || t1 === null || t1 < t0) return null;
  return Math.round((t1 - t0) / 86_400_000) + 1;
}

// ── Per-record day count ──────────────────────────────────────────────────────

/**
 * Compute the duration of a single sea-service record in days.
 *
 * Two sources of truth, in priority order:
 *   1. DG profile records carry official article months/days signed by DG
 *      Shipping — use them when present.
 *   2. CV-sourced records typically lack article fields; fall back to
 *      calculating the difference between sign-on and sign-off dates.
 */
export function serviceCalcDays(service: SeaGoingService): number {
  // DG profile path: official article fields present
  if (service.articleMonths != null || service.articleDays != null) {
    return (service.articleMonths ?? 0) * 30 + (service.articleDays ?? 0);
  }
  // CV path: calculate from actual dates
  const days = calcServiceDaysFromDates(service.serviceFrom, service.serviceTo);
  return days ?? 0;
}

// ── Overlap detection ─────────────────────────────────────────────────────────

export type SeaServiceOverlap = {
  /** Index of the first record in the array (lower index). */
  a: number;
  /** Index of the second record. */
  b: number;
  aShip: string;
  bShip: string;
  aFrom: string;
  aTo: string;
  bFrom: string;
  bTo: string;
  /** Inclusive days of overlap (approximate for ongoing records). */
  overlapDays: number;
  /** True when record A has no sign-off date (seafarer may still be on this vessel). */
  aOngoing: boolean;
  /** True when record B has no sign-off date. */
  bOngoing: boolean;
};

/**
 * Find pairs of sea-service entries whose date ranges overlap.
 *
 * A seafarer cannot sail on two different ships at the same time.
 * Genuine duplicates (same voyage) are already collapsed by deduplication
 * in profile-merge; any overlap that reaches here is a real conflict.
 *
 * Only entries with a parseable serviceFrom are tested.
 * - If both records have a closed serviceTo, a real-date overlap is a definite conflict.
 * - If one record is ongoing (no serviceTo), we still detect overlap against the
 *   other record's closed range and mark `aOngoing`/`bOngoing` so the UI can
 *   warn "verify active assignment" rather than asserting a hard error.
 * - Same-day turnarounds (e.g. sign-off and new sign-on on the same date) are
 *   excluded: overlap must be > 1 day to avoid false positives.
 */
export function findSeaServiceOverlaps(services: SeaGoingService[]): SeaServiceOverlap[] {
  const today = Date.now();

  const ranges = services.map((s) => {
    const from = parseDdMmYyyy(s.serviceFrom);
    const ongoing = !s.serviceTo || !s.serviceTo.trim();
    const to = ongoing ? today : parseDdMmYyyy(s.serviceTo);
    return { from, to, ongoing };
  });

  const overlaps: SeaServiceOverlap[] = [];

  for (let i = 0; i < services.length; i++) {
    const { from: aFrom, to: aTo, ongoing: aOngoing } = ranges[i];
    if (aFrom === null || aTo === null) continue;

    for (let j = i + 1; j < services.length; j++) {
      const { from: bFrom, to: bTo, ongoing: bOngoing } = ranges[j];
      if (bFrom === null || bTo === null) continue;

      const overlapStart = Math.max(aFrom, bFrom);
      const overlapEnd = Math.min(aTo, bTo);
      if (overlapStart >= overlapEnd) continue; // no overlap, or same-day only

      const overlapDays = Math.round((overlapEnd - overlapStart) / 86_400_000);
      if (overlapDays < 2) continue; // tolerate same-day turnarounds

      overlaps.push({
        a: i,
        b: j,
        aShip: services[i].shipName || 'Unknown vessel',
        bShip: services[j].shipName || 'Unknown vessel',
        aFrom: services[i].serviceFrom,
        aTo: services[i].serviceTo,
        bFrom: services[j].serviceFrom,
        bTo: services[j].serviceTo,
        overlapDays,
        aOngoing,
        bOngoing,
      });
    }
  }

  return overlaps;
}
